import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { SearchProvider } from "@/contexts/SearchContext";
import { LiveFilterProvider } from "@/contexts/LiveFilterContext";
import { GlobalSearchMount } from "@/components/search/GlobalSearchMount";
import { TelemetryFlushMount } from "@/components/TelemetryFlushMount";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OnboardingProvider } from "@/components/onboarding/OnboardingProvider";
import { GuideOverlay } from "@/components/onboarding/GuideOverlay";
import { OnboardingMount } from "@/components/onboarding/OnboardingMount";
import { BanAppealMount } from "@/components/shell/BanAppealMount";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { AnalyticsBoot } from "@/components/AnalyticsBoot";
import { EmailVerifyBanner } from "@/components/auth/EmailVerifyBanner";
import { SiteFooter } from "@/components/SiteFooter";
import { TermsBumpMount } from "@/components/legal/TermsBumpMount";
import { logger } from "@/lib/logger";
import Index from "./pages/Index";
import RootRouter from "./pages/RootRouter";

// Splash kodsplittas — anonyma besökare som lands:ar på /flow-weaver
// direkt (bookmark eller logged-in users) ska inte ladda ner splash-
// bundle:n (inkl. 3 demo-canvases + HeroCanvas).
const Splash = lazy(() => import("./pages/Splash"));

// Q-003: Ovanliga routes (admin, deploy-wizards, billing, 404) kodsplittas
// så att anonyma besökare + canvas-sessioner slipper ladda ner dem.
// `Index` hålls eager — det är huvud-appen och alltid varm.
// Publish refresh: tvingar ny frontend-build för custom domain env injection.
const AdminPage = lazy(() => import("./pages/Admin"));
const Deploy = lazy(() => import("./pages/Deploy"));
const DeployTrueNAS = lazy(() => import("./pages/DeployTrueNAS"));
const DeployProxmox = lazy(() => import("./pages/DeployProxmox"));
// /pricing now renders Stripe's hosted pricing table (PricingTablePage).
// The previous custom multi-provider page is parked in PricingPage.tsx.
const PricingPage = lazy(() =>
  import("./components/billing/PricingTablePage").then((m) => ({ default: m.PricingTablePage })),
);
const BillingPanel = lazy(() =>
  import("./components/billing/BillingPanel").then((m) => ({ default: m.BillingPanel })),
);
const CheckoutPage = lazy(() =>
  import("./components/billing/CheckoutPage").then((m) => ({ default: m.CheckoutPage })),
);
const Preview = lazy(() => import("./pages/Preview"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Security = lazy(() => import("./pages/Security"));
const Changelog = lazy(() => import("./pages/Changelog"));

// Atlas — multi-document workspace. Phase 2 entry point.
// Code-split because most users stay on /flow-weaver and don't need
// the atlas browser bundle (WorkspaceBrowser + sub-components).
// Atlas + Search Canvas live in the standalone /Atlas app.

// Process Engine — read-only consumer of Flow-weaver + Atlas;
// binds API actions to blocks and runs attestflows / provisioning.
// Code-split: Pro+ feature, most users won't load this bundle.
// Process Engine + /final live in the standalone /ProcessEngine app.

function RouteFallback() {
  return (
    <div className="h-svh flex items-center justify-center bg-background text-sm text-muted-foreground">
      Laddar…
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * Math.pow(2, attempt), 10000),
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
      onError: (error) => {
        logger.error('[QueryClient] Mutation error:', error);
      },
    },
  },
});

const App = () => (
  <ErrorBoundary level="app">
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
        <AuthProvider>
        <ImpersonationProvider>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ImpersonationBanner />
            <AnalyticsBoot />
            <EmailVerifyBanner />
            {/* A-035: BanAppealMount inuti LanguageProvider + TooltipProvider så
                framtida appeal-UI får tillgång till t() / Tooltip / Toaster utan
                provider-saknad. Mountas mellan Toaster och BrowserRouter eftersom
                den inte använder routing-context. */}
            <BanAppealMount />
            <SearchProvider>
            <LiveFilterProvider>
            <BrowserRouter>
              {/* TermsBumpMount renders a dialog that uses <Link>, so it MUST
                  live inside BrowserRouter. */}
              <TermsBumpMount />
              <CookieConsentBanner />


              <OnboardingProvider>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    {/*
                      A-003: per-route ErrorBoundary med level="section". En
                      crash i admin-panel / deploy-wizard / canvas ska inte
                      ta ner hela app:en — app-boundary högre upp fångar
                      fortfarande som sista-utväg.
                    */}
                    {/* `/` är tom — RootRouter redirectar till /flow-weaver
                        (default) eller /splash (när VITE_SPLASH_AS_DEFAULT=true). */}
                    <Route path="/" element={<RootRouter />} />
                    <Route path="/flow-weaver" element={<ErrorBoundary level="section"><Index /></ErrorBoundary>} />
                    <Route path="/splash" element={<ErrorBoundary level="section"><Splash /></ErrorBoundary>} />
                    <Route path="/admin" element={<ErrorBoundary level="section"><AdminPage /></ErrorBoundary>} />
                    {/* S-030: pricing och billing wrap:as i samma ErrorBoundary
                        som övriga routes — fetch-fail i paddle/stripe-init eller
                        billing-RPC ska inte falla genom till app-level boundary. */}
                    <Route path="/pricing" element={<ErrorBoundary level="section"><PricingPage /></ErrorBoundary>} />
                    <Route path="/checkout" element={<ErrorBoundary level="section"><CheckoutPage /></ErrorBoundary>} />
                    <Route path="/billing" element={<ErrorBoundary level="section"><BillingPanel /></ErrorBoundary>} />
                    <Route path="/reset-password" element={<ErrorBoundary level="section"><ResetPassword /></ErrorBoundary>} />
                    <Route path="/privacy" element={<ErrorBoundary level="section"><Privacy /></ErrorBoundary>} />
                    <Route path="/terms" element={<ErrorBoundary level="section"><Terms /></ErrorBoundary>} />
                    <Route path="/legal/security" element={<ErrorBoundary level="section"><Security /></ErrorBoundary>} />
                    <Route path="/changelog" element={<ErrorBoundary level="section"><Changelog /></ErrorBoundary>} />
                    <Route path="/deploy" element={<ErrorBoundary level="section"><Deploy /></ErrorBoundary>} />
                    <Route path="/deploy/truenas" element={<ErrorBoundary level="section"><DeployTrueNAS /></ErrorBoundary>} />
                    <Route path="/deploy/proxmox" element={<ErrorBoundary level="section"><DeployProxmox /></ErrorBoundary>} />
                    <Route path="/flow-weaver/preview" element={<ErrorBoundary level="section"><Preview /></ErrorBoundary>} />
                    <Route path="/preview" element={<Navigate to="/flow-weaver/preview" replace />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    {/* A-037: catch-all 404-route wrap:as i samma ErrorBoundary
                        som övriga 7 routes — render-error i NotFound (eller
                        Suspense-fail under lazy-load) ska stanna på section-
                        nivå, inte falla ner till app-level boundary. */}
                    <Route path="*" element={<ErrorBoundary level="section"><NotFound /></ErrorBoundary>} />
                  </Routes>
                </Suspense>
                <SiteFooter />
                <GuideOverlay />
                <OnboardingMount />
                <GlobalSearchMount />
                <TelemetryFlushMount />
              </OnboardingProvider>
            </BrowserRouter>
            </LiveFilterProvider>
            </SearchProvider>
          </TooltipProvider>
        </LanguageProvider>
        </ImpersonationProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
